import { t } from 'i18next';
import { I18N_KEYS } from '@/language/keys';
import { useGlobalStore } from '@/state/global.state';
import { ModelRunError } from '@/api/errors';
import { useWorkflowStore } from '@/state/workflow.state';
import { track } from '@/utils/analytics';
import { CompositorView } from '../Views/CompositorView';
import { useEditorStore } from '../FlowComponents/Editor/editor.state';
import { createDecomposeRunner, getDecomposeModel } from './decomposeLayers';
import { createInpaintRunner } from './inpaint';
import { NANO_BANANA_MODEL } from './nanoBanana';
import { createOutpaintRunner } from './outpaint';
import { CompositorAiError, CompositorAiErrorCode } from './compositorAiError';
import { imageSourceKey } from './imageSource';
import type { ModelRunCredits } from '@/api/modelPrediction';
import type { BatchStatus } from '@/types/batch.types';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';
import type { LayerId, NodeId } from 'web';
import type { FlowGraph } from '../FlowGraph';
import type { DecomposeLayerParams } from './decomposeLayers';
import type { InpaintParams } from './inpaint';
import type { OutpaintParams } from './outpaint';

export type { CompositorAiActionKind } from 'web';
export interface CompositorAiTarget {
  nodeId: NodeId;
  layerId: LayerId;
}
export type CompositorAiRun =
  | {
      action: 'decompose-layer';
      params: DecomposeLayerParams;
    }
  | {
      action: 'inpaint';
      params: InpaintParams;
    }
  | {
      action: 'outpaint';
      params: OutpaintParams;
    };
interface CompositorAiTaskBase extends CompositorAiTarget {
  id: string;
  status: BatchStatus;
  progress: number;
  phase?: 'applying';
  startedAt: number;
  error?: unknown;
}

export type CompositorAiTask = CompositorAiTaskBase & CompositorAiRun;

interface PendingTask {
  task: CompositorAiTask;
  cancel?: () => Promise<void>;
  analytics: {
    model_id: string;
    image_width?: number;
    image_height?: number;
    layer_count?: number;
  };
}

export function isActiveTask(task: CompositorAiTask) {
  return task.status === 'RUNNING' || task.status === 'QUEUED';
}

/** In-memory work owned by one FlowGraph, never by a mounted action panel. */
export class CompositorAiTasks {
  private pending = new Map<string, PendingTask>();
  private tasks: readonly CompositorAiTask[] = [];
  private listeners = new Set<() => void>();
  private lifetime = new AbortController();
  private unsubscribe: () => void;
  private unsubscribeContent: () => void;
  private workflowId = useWorkflowStore.getState().recipe.id;
  private workspaceId = useWorkflowStore.getState().recipe.workspaceId;
  onCredits?: (credits: ModelRunCredits) => void;
  onError?: (task: CompositorAiTask) => void;

  constructor(private graph: FlowGraph) {
    this.unsubscribe = useWorkflowStore.subscribe(() => {
      if (!this.isCurrent()) this.dispose();
    });
    this.unsubscribeContent = graph.store.addContentListener(() => {
      let removed = false;
      for (const [id, { task }] of this.pending) {
        const compositor = graph.store.getNodeDataNullable<CompositorNodeV3>(task.nodeId);
        if (compositor?.data.layers[task.layerId]) continue;
        this.pending.delete(id);
        new CompositorView(graph, task.nodeId).onDoneCompositorAiAction(task.layerId, true);
        removed = true;
      }
      if (removed) {
        this.tasks = [...this.pending.values()].map(({ task }) => task);
        this.listeners.forEach((listener) => listener());
      }
    });
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getSnapshot = () => this.tasks;

  getLatest(target: CompositorAiTarget) {
    return this.tasks.findLast((task) => task.nodeId === target.nodeId && task.layerId === target.layerId);
  }

  private isCurrent() {
    const { recipe } = useWorkflowStore.getState();
    return !this.lifetime.signal.aborted && recipe.id === this.workflowId && recipe.workspaceId === this.workspaceId;
  }

  private updateTask(pending: PendingTask, patch: Partial<CompositorAiTaskBase>) {
    if (!this.isCurrent() || !this.pending.has(pending.task.id)) return;
    const wasActive = this.tasks.some((task) => task.id === pending.task.id && isActiveTask(task));
    pending.task = { ...pending.task, ...patch };
    if (wasActive && !isActiveTask(pending.task)) {
      const view = new CompositorView(this.graph, pending.task.nodeId);
      view.onDoneCompositorAiAction(pending.task.layerId, pending.task.status === 'COMPLETED');
    }
    if (pending.task.status === 'COMPLETED') {
      for (const [id, { task }] of this.pending) {
        if (task.nodeId === pending.task.nodeId && task.layerId === pending.task.layerId) this.pending.delete(id);
      }
    }
    this.tasks = [...this.pending.values()].map(({ task }) => task);
    this.listeners.forEach((listener) => listener());
  }

  async start(target: CompositorAiTarget, run: CompositorAiRun) {
    if (!this.isCurrent()) return;
    if (
      this.tasks.some((task) => task.nodeId === target.nodeId && task.layerId === target.layerId && isActiveTask(task))
    )
      return;
    const pending: PendingTask = {
      task: {
        ...target,
        id: crypto.randomUUID(),
        ...structuredClone(run),
        status: 'RUNNING',
        progress: 0,
        startedAt: Date.now(),
      },
      analytics: {
        model_id: run.action === 'decompose-layer' ? getDecomposeModel(run.params.model).name : NANO_BANANA_MODEL.name,
      },
    };
    // Acquire the layer synchronously, before any preparation or paid request.
    this.pending.set(pending.task.id, pending);
    // Count accepted attempts even if the workflow closes before a result arrives.
    this.track(pending, 'started');
    try {
      const view = new CompositorView(this.graph, target.nodeId);
      const submission = view.onSubmitCompositorAiAction();
      this.updateTask(pending, {});
      if (useWorkflowStore.getState().workflowRole !== 'editor') {
        throw new CompositorAiError(CompositorAiErrorCode.ApplyFailed);
      }
      const source = structuredClone(view.getImageSource(target.layerId));
      if (!source) throw new CompositorAiError(CompositorAiErrorCode.SourceUnavailable);
      pending.analytics.image_width = source.file.width;
      pending.analytics.image_height = source.file.height;
      const sourceKey = imageSourceKey(source);
      const onCredits = (credits: ModelRunCredits) => {
        if (this.isCurrent()) this.onCredits?.(credits);
      };
      const observation = {
        signal: this.lifetime.signal,
        onProgress: (progress: number) => {
          if (!isActiveTask(pending.task)) return;
          // Model completion still leaves result preparation and the canvas import to finish.
          this.updateTask(pending, { progress: Math.min(progress, 99) });
        },
        onCancelReady: (cancel: () => Promise<void>) => {
          pending.cancel = cancel;
        },
        keepTracking: true,
      };
      let images;
      switch (run.action) {
        case 'outpaint':
          images = [
            await createOutpaintRunner(
              () => source,
              () => (submission?.type === 'outpaint' ? submission : undefined),
              onCredits,
            )({ ...target, runId: pending.task.id, prompt: run.params.prompt }, observation),
          ];
          break;
        case 'inpaint':
          images = [
            await createInpaintRunner(
              () => source,
              () => (submission?.type === 'inpaint' ? submission.mask : undefined),
              onCredits,
            )({ ...target, runId: pending.task.id, prompt: run.params.prompt }, observation),
          ];
          break;
        case 'decompose-layer':
          images = await createDecomposeRunner(() => source, onCredits)(
            { ...target, runId: pending.task.id, model: run.params.model, numberOfLayers: run.params.numberOfLayers },
            observation,
          );
          break;
      }
      if (!this.isCurrent() || !this.pending.has(pending.task.id) || !isActiveTask(pending.task)) return;
      pending.analytics.layer_count = images.length;
      this.updateTask(pending, { phase: 'applying' });
      const currentSource = view.getImageSource(target.layerId);
      if (!currentSource || imageSourceKey(currentSource) !== sourceKey) {
        throw new CompositorAiError(CompositorAiErrorCode.SourceChanged);
      }
      const layer = this.graph.store.getNodeData<CompositorNodeV3>(target.nodeId).data.layers[target.layerId];
      const resultName = `${run.params.groupName} – ${layer.name}`;
      if (run.action !== 'decompose-layer') {
        images = images.map((image) => ({ ...image, name: resultName }));
      }
      const selectedLayers = this.graph.getUIState().compositor[target.nodeId]?.selectedLayers;
      const selectResult = selectedLayers?.length === 1 && selectedLayers[0] === target.layerId;
      const resultLayerId = view.importImages(images, resultName, target.layerId, {
        wrapInGroup: run.action === 'decompose-layer',
        hideSourceLayer: true,
        customBounds: run.action === 'outpaint' && submission?.type === 'outpaint' ? submission.bounds : undefined,
      });
      this.updateTask(pending, { status: 'COMPLETED', progress: 100 });
      if (selectResult) view.setSelectedLayerIds([resultLayerId]);
      this.track(pending, 'success');
      this.graph.loadResources();
      useGlobalStore.getState().updateSnackbarData({
        isOpen: true,
        severity: 'success',
        text: t(I18N_KEYS.COMPOSITOR.AI_ACTIONS.SUCCEEDED, { action: run.params.groupName }),
        action: {
          text: t(I18N_KEYS.COMPOSITOR.AI_ACTIONS.GO_TO_LAYER),
          onClick: () => {
            useGlobalStore.getState().updateSnackbarData({ isOpen: false });
            if (!this.isCurrent()) return;
            const compositor = this.graph.store.getNodeDataNullable<CompositorNodeV3>(target.nodeId);
            if (!compositor?.data.layers[resultLayerId]) return;
            const editor = useEditorStore.getState();
            if (!editor.isEditorOpen || editor.nodeId !== target.nodeId) {
              editor.openEditor(target.nodeId);
              view.enableNodeEditor();
              view.setUndoBarrier();
            }
            view.setSelectedLayerIds([resultLayerId]);
            view.zoomToLayer(resultLayerId);
          },
        },
      });
    } catch (error) {
      if (!this.isCurrent() || !this.pending.has(pending.task.id) || !isActiveTask(pending.task)) return;
      this.fail(pending, error);
    }
  }

  async cancel(target: CompositorAiTarget) {
    if (!this.isCurrent()) return;
    const pending = [...this.pending.values()].find(
      ({ task }) => task.nodeId === target.nodeId && task.layerId === target.layerId && isActiveTask(task),
    );
    if (!pending?.cancel) return;
    // Release the layer and reject late results before waiting for server cancellation.
    this.fail(pending, new ModelRunError('canceled'));
    try {
      await pending.cancel();
    } catch {
      if (!this.isCurrent()) return;
      this.onError?.({
        ...pending.task,
        error: new CompositorAiError(CompositorAiErrorCode.CancellationFailed),
      });
    }
  }

  private fail(pending: PendingTask, error: unknown) {
    this.updateTask(pending, {
      status: error instanceof ModelRunError && error.kind === 'canceled' ? 'CANCELED' : 'FAILED',
      error,
    });
    if (pending.task.status === 'FAILED') this.onError?.(pending.task);
    this.track(pending, pending.task.status === 'CANCELED' ? 'canceled' : 'failed');
  }

  private track({ task, analytics }: PendingTask, outcome: 'started' | 'success' | 'failed' | 'canceled') {
    track(outcome === 'started' ? 'compositor_ai_run_started' : 'compositor_ai_action_run', {
      execution_origin: 'compositor_action',
      action_id: task.action,
      run_id: task.id,
      workflow_id: this.workflowId,
      node_id: task.nodeId,
      layer_id: task.layerId,
      ...analytics,
      ...(outcome === 'started' ? {} : { outcome, duration_ms: Date.now() - task.startedAt }),
    });
  }

  dispose() {
    if (this.lifetime.signal.aborted) return;
    this.lifetime.abort();
    this.unsubscribe();
    this.unsubscribeContent();
    this.listeners.clear();
    for (const { task } of this.pending.values()) {
      if (isActiveTask(task)) new CompositorView(this.graph, task.nodeId).onDoneCompositorAiAction(task.layerId, false);
    }
    this.pending.clear();
    this.tasks = [];
    this.onCredits = undefined;
    this.onError = undefined;
  }
}
